import {
  type CampaignBriefDeliverable,
  type CampaignBriefFaqEntry,
  type CampaignBriefPastResult,
  type CampaignBriefStructured,
  type CampaignBriefTimelineEntry,
} from "@/marketing/models/influencerCampaign";

export interface DeliverableRow {
  type: string;
  count: string;
  description: string;
}

export interface TimelineRow {
  label: string;
  date: string;
}

export interface FaqRow {
  question: string;
  answer: string;
}

export interface PastResultRow {
  label: string;
  metric: string;
}

export interface BriefStructuredFormState {
  requiredHashtagsRaw: string;
  prohibitedRaw: string;
  minFollowers: string;
  minTier: string;
  deliverables: DeliverableRow[];
  timeline: TimelineRow[];
  faq: FaqRow[];
  pastResults: PastResultRow[];
  thumbnailUrl?: string | undefined;
  imagesRaw: string;
  brandLookForRaw: string;
  usageFreedom: string;
  usagePeriod: string;
  usageScope: string;
}

export function emptyBriefStructuredForm(): BriefStructuredFormState {
  return {
    requiredHashtagsRaw: "",
    prohibitedRaw: "",
    minFollowers: "",
    minTier: "",
    deliverables: [],
    timeline: [],
    faq: [],
    pastResults: [],
    thumbnailUrl: undefined,
    imagesRaw: "",
    brandLookForRaw: "",
    usageFreedom: "",
    usagePeriod: "",
    usageScope: "",
  };
}

export function readBriefStructuredForm(
  bs: CampaignBriefStructured | undefined,
): BriefStructuredFormState {
  return {
    requiredHashtagsRaw: (bs?.requiredHashtags ?? []).join(", "),
    prohibitedRaw: (bs?.prohibited ?? []).join(", "),
    minFollowers:
      bs?.eligibility?.minFollowers != null ? String(bs.eligibility.minFollowers) : "",
    minTier: bs?.eligibility?.minTier != null ? String(bs.eligibility.minTier) : "",
    deliverables: (bs?.deliverables ?? []).map((d) => ({
      type: d.type ?? "",
      count: d.count != null ? String(d.count) : "",
      description: d.description ?? "",
    })),
    timeline: (bs?.timeline ?? []).map((t) => ({
      label: t.label ?? "",
      date: t.date ? t.date.slice(0, 10) : "",
    })),
    faq: (bs?.faq ?? []).map((q) => ({
      question: q.question ?? "",
      answer: q.answer ?? "",
    })),
    pastResults: (bs?.pastResults ?? []).map((p) => ({
      label: p.label ?? "",
      metric: p.metric ?? "",
    })),
    thumbnailUrl: bs?.images?.[0],
    imagesRaw: (bs?.images ?? []).slice(1).join("\n"),
    brandLookForRaw: (bs?.brandLookFor ?? []).join("\n"),
    usageFreedom: bs?.usageRights?.freedom ?? "",
    usagePeriod: bs?.usageRights?.usagePeriod ?? "",
    usageScope: bs?.usageRights?.scope ?? "",
  };
}

function splitCsv(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

// brandLookFor criteria are sentence-like (may contain commas), so split on newlines only.
function splitLines(raw: string): string[] {
  return raw
    .split(/\n+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function buildBriefStructured(
  coreMessage: string,
  form: BriefStructuredFormState,
): CampaignBriefStructured | undefined {
  const requiredHashtags = splitCsv(form.requiredHashtagsRaw);
  const prohibited = splitCsv(form.prohibitedRaw);
  const images = [form.thumbnailUrl?.trim(), ...splitCsv(form.imagesRaw)].filter(
    (entry): entry is string => Boolean(entry),
  );
  const deliverables: CampaignBriefDeliverable[] = form.deliverables
    .filter((row) => row.type.trim())
    .map((row) => {
      const entry: CampaignBriefDeliverable = { type: row.type.trim() };
      if (row.count.trim()) entry.count = Number(row.count);
      if (row.description.trim()) entry.description = row.description.trim();
      return entry;
    });
  const timeline: CampaignBriefTimelineEntry[] = form.timeline
    .filter((row) => row.label.trim())
    .map((row) => {
      const entry: CampaignBriefTimelineEntry = { label: row.label.trim() };
      if (row.date.trim()) entry.date = new Date(`${row.date}T12:00:00`).toISOString();
      return entry;
    });
  const faqEntries: CampaignBriefFaqEntry[] = form.faq
    .filter((row) => row.question.trim() && row.answer.trim())
    .map((row) => ({ question: row.question.trim(), answer: row.answer.trim() }));
  const pastResultEntries: CampaignBriefPastResult[] = form.pastResults
    .filter((row) => row.label.trim())
    .map((row) => {
      const entry: CampaignBriefPastResult = { label: row.label.trim() };
      if (row.metric.trim()) entry.metric = row.metric.trim();
      return entry;
    });

  const result: CampaignBriefStructured = {};
  const trimmedCoreMessage = coreMessage.trim();
  if (trimmedCoreMessage) result.coreMessage = trimmedCoreMessage;
  if (images.length > 0) result.images = images;
  if (requiredHashtags.length > 0) result.requiredHashtags = requiredHashtags;
  if (prohibited.length > 0) result.prohibited = prohibited;
  if (deliverables.length > 0) result.deliverables = deliverables;
  if (timeline.length > 0) result.timeline = timeline;
  if (faqEntries.length > 0) result.faq = faqEntries;
  if (pastResultEntries.length > 0) result.pastResults = pastResultEntries;
  const minFollowersNum = form.minFollowers.trim()
    ? Number(form.minFollowers)
    : undefined;
  const minTierNum = form.minTier.trim() ? Number(form.minTier) : undefined;
  if (minFollowersNum !== undefined || minTierNum !== undefined) {
    result.eligibility = {};
    if (minFollowersNum !== undefined) result.eligibility.minFollowers = minFollowersNum;
    if (minTierNum !== undefined) result.eligibility.minTier = minTierNum;
  }
  const brandLookFor = splitLines(form.brandLookForRaw);
  if (brandLookFor.length > 0) result.brandLookFor = brandLookFor;
  const freedom = form.usageFreedom.trim();
  const usagePeriod = form.usagePeriod.trim();
  const scope = form.usageScope.trim();
  if (freedom || usagePeriod || scope) {
    result.usageRights = { freedom, usagePeriod, scope };
  }

  return result;
}
